import { Controller } from "@hotwired/stimulus"
import debounce from "lodash.debounce"

const DEBOUNCE_TIMING = 300

export default class extends Controller {
  static targets = ["textarea", "indicator", "indicatorText", "discardButton"]

  static values = {
    storageKey: String,
  }

  connect() {
    this.saveDraft = debounce(this.#persistDraft.bind(this), DEBOUNCE_TIMING)
    this.originalValue = this.textareaTarget.value
    this.restoring = true
    this.#restoreDraft()
    this.restoring = false
    this.submitEndHandler = this.#handleSubmitEnd.bind(this)
    this.element.addEventListener("turbo:submit-end", this.submitEndHandler)
  }

  disconnect() {
    this.saveDraft.cancel()
    this.element.removeEventListener("turbo:submit-end", this.submitEndHandler)
  }

  onInput() {
    if (this.restoring) return
    this.saveDraft()
  }

  discard() {
    this.#clearDraft()
    this.restoring = true
    this.textareaTarget.value = this.originalValue
    this.#triggerResize()
    this.restoring = false
  }

  #persistDraft() {
    try {
      const text = this.textareaTarget.value
      if (text.trim().length > 0) {
        localStorage.setItem(this.storageKeyValue, text)
        this.#showSavedIndicator()
      } else {
        localStorage.removeItem(this.storageKeyValue)
        this.#hideIndicator()
      }
    } catch {
      // localStorage unavailable or full — gracefully degrade
    }
  }

  #restoreDraft() {
    try {
      const draft = localStorage.getItem(this.storageKeyValue)
      if (draft) {
        this.textareaTarget.value = draft
        this.#triggerResize()
        this.#showLoadedIndicator()
      }
    } catch {
      // localStorage unavailable — gracefully degrade
    }
  }

  #clearDraft() {
    try {
      localStorage.removeItem(this.storageKeyValue)
    } catch {
      // localStorage unavailable — gracefully degrade
    }
    this.#hideIndicator()
  }

  #showSavedIndicator() {
    if (!this.hasIndicatorTarget) return
    if (this.hasIndicatorTextTarget) {
      this.indicatorTextTarget.classList.remove("hidden")
    }
    if (this.hasDiscardButtonTarget) {
      this.discardButtonTarget.classList.add("hidden")
    }
    this.indicatorTarget.classList.remove("hidden")
  }

  #showLoadedIndicator() {
    if (!this.hasIndicatorTarget) return
    if (this.hasIndicatorTextTarget) {
      this.indicatorTextTarget.classList.add("hidden")
    }
    if (this.hasDiscardButtonTarget) {
      this.discardButtonTarget.classList.remove("hidden")
    }
    this.indicatorTarget.classList.remove("hidden")
  }

  #hideIndicator() {
    if (this.hasIndicatorTarget) {
      this.indicatorTarget.classList.add("hidden")
    }
  }

  #triggerResize() {
    this.textareaTarget.dispatchEvent(new Event("input", { bubbles: true }))
  }

  #handleSubmitEnd(event) {
    if (event.detail?.fetchResponse?.succeeded) {
      this.#clearDraft()
    }
  }
}
